import { useState, useEffect } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Wordmark } from '@/components/wordmark'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { OAuthButtons, OAuthDivider } from '@/components/oauth-buttons'
import { apiV2, ApiV2Error } from '@/lib/api-v2-client'
import { toast } from 'sonner'
import { SiteFooter } from '@/components/site-footer'
import { ArrowRight } from 'lucide-react'
import { useResendCooldown } from '@/hooks/use-resend-cooldown'
import { authRedirectTarget, linkRedirect, rememberRedirect, takeRememberedRedirect } from '@/lib/post-auth-redirect'
import { TwoFactorCodeForm } from '@/components/two-factor-code-form'
import { CheckEmailStep } from '@/components/check-email-step'
import { rememberOAuthReturn } from '@/lib/oauth-return'
import { isBillingInterval, isSignupPlan, type BillingInterval, type SignupPlan } from '@/lib/plans'

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): { error?: string; redirect?: string; plan?: SignupPlan; interval?: BillingInterval; two_factor?: string; return_to?: string } => ({
    error: (search.error as string) || undefined,
    two_factor: search.two_factor ? String(search.two_factor) : undefined,
    return_to: typeof search.return_to === 'string' ? search.return_to : undefined,
    redirect: (search.redirect as string) || undefined,
    plan: isSignupPlan(search.plan) ? search.plan : undefined,
    interval: isBillingInterval(search.interval) ? search.interval : undefined,
  }),
  component: LoginPage,
  head: () => ({
    meta: [
      { title: 'Log In — Uptrack Uptime Monitoring Dashboard' },
      { name: 'description', content: 'Sign in to your Uptrack dashboard to manage your uptime monitors, alert channels, incidents and public status pages from one place.' },
      { property: 'og:title', content: 'Log In — Uptrack Uptime Monitoring Dashboard' },
      { property: 'og:description', content: 'Sign in to your Uptrack dashboard to manage your uptime monitors, alert channels, incidents and public status pages.' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/login' },
    ],
  }),
})

function LoginPage() {
  const { two_factor } = Route.useSearch()
  return two_factor ? <TwoFactorStep /> : <LoginForm />
}

// Google/GitHub sign-in for a user with 2FA ends here: the server set a
// short-lived pending cookie; the code completes the sign-in.
function TwoFactorStep() {
  async function submit(code: string) {
    try {
      await apiV2.completeTwoFactor(code)
      toast.success('Signed in!')
      // Hard navigation so the app boots with the new session cookie.
      window.location.href = takeRememberedRedirect() ?? '/dashboard'
    } catch (err) {
      toast.error(err instanceof ApiV2Error ? err.message : 'Invalid 2FA code')
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <main id="main" className="flex flex-1 items-start justify-center px-4 py-20">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <h1 data-slot="card-title" className="text-2xl leading-none font-semibold">Two-factor authentication</h1>
            <CardDescription>Enter the code from your authenticator app, or a backup code.</CardDescription>
          </CardHeader>
          <CardContent>
            <TwoFactorCodeForm onSubmit={submit} />
          </CardContent>
          <CardFooter className="justify-center">
            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
              Sign in another way
            </Link>
          </CardFooter>
        </Card>
      </main>
      <SiteFooter />
    </div>
  )
}

export function LoginForm() {
  const { error, redirect: redirectTo, plan, interval, return_to } = Route.useSearch()
  // MCP OAuth sends ?return_to=<API>/oauth/authorize?...; resume it after sign-in.
  const oauthResume = rememberOAuthReturn(return_to)
  const target = oauthResume ? '/oauth/resume' : authRedirectTarget({ redirect: redirectTo, plan, interval })

  // The emailed link carries a short redirect only (the server drops over 200 bytes).
  // A longer one, such as the OAuth consent URL, comes back from this browser's storage.
  const linkTarget = linkRedirect(target)

  // Store redirect URL for post-auth navigation (survives OAuth redirects)
  useEffect(() => {
    rememberRedirect(target)
  }, [target])

  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [sending, setSending] = useState(false)
  const cooldown = useResendCooldown()

  useEffect(() => {
    if (error) {
      const messages: Record<string, string> = {
        auth_failed: 'Authentication failed. Please try again.',
        no_email: 'No email address was provided by the OAuth provider.',
        signup_failed: 'Account creation failed. Please try again.',
        disposable_email: 'This email domain is not accepted. Use another email address.',
        signup_rate_limited: 'Too many sign-ups from this network today. Try again tomorrow.',
      }
      toast.error(messages[error] || 'Something went wrong. Please try again.')
    }
  }, [error])

  async function handleSendMagicLink(e: React.FormEvent) {
    e.preventDefault()
    if (!email || sending) return
    setSending(true)
    try {
      await apiV2.requestMagicLink(email, linkTarget)
      setSent(true)
      cooldown.start()
      toast.success('Sign-in code and link sent!')
    } catch (err) {
      if (err instanceof ApiV2Error && err.status === 429) {
        toast.error('Too many requests. Try again in a few minutes.')
      } else {
        toast.error('Failed to send the sign-in email.')
      }
    } finally {
      setSending(false)
    }
  }

  async function handleResend() {
    setSending(true)
    try {
      await apiV2.requestMagicLink(email, linkTarget)
      toast.success('Sign-in code and link resent!')
      cooldown.start()
    } catch {
      toast.error('Failed to resend.')
    } finally {
      setSending(false)
    }
  }

  // "Check your email" screen: a code to type, and the link in the same email
  if (sent) {
    return (
      <CheckEmailStep
        email={email}
        redirect={linkTarget}
        sending={sending}
        cooldownRemaining={cooldown.remaining}
        onResend={handleResend}
        onBack={() => { setSent(false); setEmail('') }}
      />
    )
  }

  return (
    <div className="flex min-h-screen flex-col">
      <main id="main" className="flex flex-1 items-start justify-center px-4 py-20">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Link to="/" aria-label="Uptrack home" className="mx-auto mb-2 inline-flex"><Wordmark height={28} /></Link>
            <h1 data-slot="card-title" className="text-2xl leading-none font-semibold">Welcome back</h1>
            <CardDescription>Sign in to your Uptrack account</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleSendMagicLink} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={sending}
                  required
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full" disabled={sending || !email}>
                {sending ? 'Sending...' : (
                  <>Send sign-in code <ArrowRight className="ml-2 h-4 w-4" /></>
                )}
              </Button>
            </form>
            <OAuthDivider />
            <OAuthButtons />
          </CardContent>
          <CardFooter className="justify-center pt-6">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" search={{ redirect: redirectTo, plan, interval }} className="text-primary hover:underline">Sign up</Link>
            </p>
          </CardFooter>
        </Card>
      </main>
      <SiteFooter />
    </div>
  )
}
